/*
 * Civia - in-place product swap for the shape and metal selectors (round 89).
 *
 * The problem this solves
 * ----------------------
 * Shape (`snippets/diamond-weight.liquid`) and metal (`snippets/metal-color.liquid`)
 * are not variant options on this store - each combination is its own product, and
 * both snippets build their links by rewriting the metal/shape token inside the
 * product handle. Every click was therefore a full document navigation: the whole
 * page tore down and rebuilt just to change one photo, one title and one price.
 *
 * What this does instead
 * ----------------------
 * Intercepts those clicks, fetches only the main product section for the target
 * product, and swaps it into the page. The URL, the page title and the selector's
 * own active state all update, so the result is indistinguishable from a navigation
 * except that nothing flashes and the scroll position is kept.
 *
 * Why a section swap rather than patching individual nodes
 * -------------------------------------------------------
 * Nearly everything in the product view differs between siblings - gallery, title,
 * price, variant JSON, SKU, inventory map, the selectors' own active states. Letting
 * the server render the whole section is both simpler and less likely to drift than
 * hand-patching a dozen nodes, and it means the active states come back correct for
 * free instead of being recomputed on the client.
 *
 * Why the re-initialisation is safe
 * ---------------------------------
 * The product view is built from web components (`variant-radios`, `product-form`,
 * `sticky-add-to-cart`, `product-tab`, `complementary-products`, `breadcrumb-component`).
 * Their classes are registered once, globally, so freshly injected instances upgrade
 * themselves through `connectedCallback` with no help from us. That leaves only the
 * section's own inline scripts, which are re-executed below - see `runScripts`.
 *
 * Degradation
 * -----------
 * Every failure path ends in a real navigation to the href, which is exactly the
 * behaviour that existed before this file. A modified click (new tab, middle click)
 * is never intercepted. No JS, no fetch, a slow network, a non-200, an unexpected
 * document shape: the shopper still gets the product page.
 */
(function () {
  'use strict';

  if (window.__civiaOptionSwap) return;

  /* Anything missing here and the links stay plain links, which is the pre-round-89
     behaviour - a working page, just with the reload. Checked before the flag is
     set so nothing else assumes this file took over. */
  if (
    !window.fetch ||
    !window.Promise ||
    !window.Map ||
    !window.URL ||
    !window.DOMParser ||
    !window.history ||
    !history.pushState ||
    !Element.prototype.closest
  ) {
    return;
  }

  window.__civiaOptionSwap = true;

  var LINKS = 'a.dw-option, a.metal-option';
  var MAIN = '[id^="shopify-section-"][id$="__main"]';
  var PREFETCH_MAX = 12;

  /* Round 90 - the same idea on the collection grid. A card's shape and metal
     rows (`a.variant-option`) are sibling-product links exactly like the ones on
     the product page, so they navigated too. Here only the one card is replaced
     and the page URL is deliberately left alone: the shopper stays where they
     were in the grid, at the same scroll position, with every other card
     untouched. Because the replacement card is server-rendered, its `card-link`
     already points at the newly chosen product - so tapping through opens the
     shape they just picked. */
  var CARD_LINKS = 'a.variant-option';
  var CARD = '.product-item';
  var CARD_VIEW = 'view=collection-card';

  /*
   * Round 91 - the scripts that have to run again after the product section is
   * replaced.
   *
   * These are loaded from snippets/global-script-2.liquid, OUTSIDE the section,
   * and each is an IIFE that runs once on page load and binds to, or injects
   * into, the product markup it finds. Round 89 replaced that markup underneath
   * them and never re-ran them, so after a shape or metal change the gallery
   * thumbnails stopped driving the main image and the sale banner, Save chip,
   * shipping estimate and sticky Add to Cart contents silently vanished.
   *
   * Re-executing them is safe because not one of them guards on a global flag -
   * every one marks the element it has already handled:
   *
   *   civia-pdp-gallery      root.__civiaGallery on the gallery root
   *   civia-price-display    data-civia-save
   *   civia-sale-banner      data-civia-sale-banner
   *   civia-ship-estimate    data-civia-ship-estimate
   *   civia-sticky-atc       per-node, plus a STYLE_ID guard on its stylesheet
   *   civia-sample-reviews-pdp  data-civia-pdp-reviews
   *
   * The marked elements died with the old section, so a re-run injects exactly
   * once into the new one. Their <style> blocks are id-guarded and are not
   * duplicated. Anything whose target sits OUTSIDE the section is deliberately
   * absent from this list - civia-call.js would append a second floating button.
   *
   * A script added here must be idempotent in the same way. Check its guard.
   */
  var RERUN = [
    'civia-pdp-gallery',
    'civia-price-display',
    'civia-sale-banner',
    'civia-ship-estimate',
    'civia-sticky-atc',
    'civia-sample-reviews-pdp'
  ];

  var cache = new Map();
  var busy = false;

  /* "Silver Oval Cut ... - civia jewels" -> " - civia jewels", so a swapped title
     keeps the store name the theme puts there. Empty if the theme has no suffix. */
  var SUFFIX = (function () {
    var match = document.title.match(/\s[–—|-]\s[^–—|-]*$/);
    return match ? match[0] : '';
  })();

  /* --- plumbing ---------------------------------------------------------- */

  function section() {
    return document.querySelector(MAIN);
  }

  /* The section id the Section Rendering API expects: the element id with
     Shopify's `shopify-section-` prefix removed. */
  function sectionId() {
    var el = section();
    return el ? el.id.replace(/^shopify-section-/, '') : null;
  }

  function samePath(href) {
    try {
      return new URL(href, location.origin).pathname === location.pathname;
    } catch (e) {
      return false;
    }
  }

  /* A response is only usable if it actually rendered a product view. A soft 404,
     a challenge page or a redirect to the homepage all return 200, so status
     alone proves nothing - the same trap round 82 hit on the newsletter form. */
  function usable(node) {
    return !!(node && node.querySelector('.productView, .productView-images, [data-json-product]'));
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* --- fetching ---------------------------------------------------------- */

  /* Primary path asks for just the section. It is the smaller request and skips
     the header, footer and every other section on the page.

     It can legitimately miss: the Section Rendering API keys on the *template*,
     so a sibling product assigned a different product template has a different
     section id and returns nothing usable. That is what the full-document
     fallback is for - slower, but it cannot be wrong about which section it got. */
  function load(url) {
    if (cache.has(url)) return Promise.resolve(cache.get(url));

    var id = sectionId();
    var sectionUrl = id
      ? url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(id)
      : null;

    var attempt = sectionUrl
      ? fetch(sectionUrl, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
          .then(function (html) {
            var doc = parse(html);
            /* A section response may or may not carry the wrapper div. */
            var node = doc.querySelector(MAIN) || doc.body;
            if (!usable(node)) return Promise.reject();
            return node;
          })
      : Promise.reject();

    return attempt
      .catch(function () {
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
          .then(function (html) {
            var doc = parse(html);
            var node = doc.querySelector(MAIN);
            if (!usable(node)) return Promise.reject();
            /* Only the full document carries a real <title>. */
            if (doc.title) node.setAttribute('data-civia-title', doc.title);
            return node;
          });
      })
      .then(function (node) {
        if (cache.size >= PREFETCH_MAX) cache.delete(cache.keys().next().value);
        cache.set(url, node);
        return node;
      });
  }

  /* --- fetching a single collection card ---------------------------------- */

  /*
   * `templates/product.collection-card.liquid` renders one grid card and nothing
   * else - `layout none`, no header, no footer, no other section.
   *
   * The suffix is load-bearing. `snippets/product-card-06.liquid` wraps the shape
   * and metal rows in `{% if template contains 'collection' %}`, so a normal
   * product view returns a card with no swatches at all. Rendering through a
   * suffix that contains "collection" makes `template` read
   * "product.collection-card", the gate passes, and the card comes back exactly
   * as the grid builds it - same badge, price, swatch rows, active states.
   */
  /* Which product a card is currently showing, read from its own link. */
  function cardPath(card) {
    var link = card.querySelector('a.card-link');
    if (!link) return null;
    try {
      return new URL(link.getAttribute('href'), location.origin).pathname;
    } catch (e) {
      return null;
    }
  }

  function loadCard(url) {
    var key = 'card:' + url;
    if (cache.has(key)) return Promise.resolve(cache.get(key));

    var cardUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + CARD_VIEW;

    return fetch(cardUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
      .then(function (html) {
        var node = parse(html).querySelector(CARD);
        /* Same reasoning as `usable`: a 200 proves nothing on its own. */
        if (!node || !node.getAttribute('data-product-id')) return Promise.reject();
        if (cache.size >= PREFETCH_MAX) cache.delete(cache.keys().next().value);
        cache.set(key, node);
        return node;
      });
  }

  /*
   * Round 93 - a swapped card is finished before the browser paints it.
   *
   * Round 90 replaced the node and relied on the rest of the page noticing:
   * civia-card.js through its MutationObserver, lazySizes through its own, the
   * row arrows through civia-card-variants.js. They did notice - not in time.
   * Captured on dev in the same task a swapped card is inserted (client video,
   * 375px, 13 Sep 2026):
   *
   *   photo    class "lazyload", no srcset - a blank white square
   *   price    "$615.00 USD" - tidied by civia-card.js 120ms later
   *   badge    missing, and the Price label and ENDS SOON with it
   *   arrows   all four showing - and STILL showing 4s later
   *
   * The arrows were never fixed, not merely late. civia-card-variants.js syncs a
   * row the first time its scan() meets it, and scan() runs on load and on
   * window scroll or resize - none of which a tap causes. Neither end of either
   * row was marked, so an arrow sat on the first and last swatch of each; on the
   * four-disc metal row, which never scrolls, it hid a metal completely.
   *
   * So the card is completed here, and inserted only once its photo is ready:
   *
   *   readyPhoto   fetch and decode the photo, hand the <img> a plain src
   *   (insert)
   *   scan()       civia-card-variants.js - prune dead options, bind + sync rows
   *   settleRows   each row where the shopper left it, the chosen swatch in view
   *   run()        civia-card.js - badge, Price label, money, rating, ENDS SOON
   *
   * Insert to run() is one task, so there is no frame in between to paint.
   *
   * A tap made while a card is still loading is not swallowed: the latest tap
   * wins, and a card that ends up back where it started is simply left alone.
   */
  var PHOTO_WAIT = 1500;
  var ARROW_ROOM = 22;

  function pathOf(url) {
    try {
      return new URL(url, location.origin).pathname;
    } catch (e) {
      return url;
    }
  }

  /* Resolves - never rejects - on load, on error or at PHOTO_WAIT. A slow photo
     only means the card goes in without it and lazySizes finishes the job,
     which is exactly how it behaved before. It must never hold a swap hostage. */
  function readyPhoto(card) {
    var img = card.querySelector('.card-media img');
    var srcset = img && img.getAttribute('data-srcset');
    var src = img && img.getAttribute('data-src');
    if (!srcset && !src) return Promise.resolve();

    return new Promise(function (resolve) {
      var probe = new Image();
      var settled = false;
      var timer;

      var finish = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);

        /* A plain src the browser already holds paints on insertion. A srcset
           is re-selected asynchronously even from cache, which can cost the very
           frame this exists to save - so srcset is only the fallback, for a
           photo that did not arrive in time. */
        if (probe.complete && probe.naturalWidth && (probe.currentSrc || probe.src)) {
          img.setAttribute('src', probe.currentSrc || probe.src);
        } else if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.setAttribute('src', src);
        }
        img.removeAttribute('loading');
        img.classList.remove('lazyload', 'lazyloading');
        img.classList.add('lazyloaded');
        resolve();
      };

      timer = setTimeout(finish, PHOTO_WAIT);
      probe.onload = function () {
        if (probe.decode) probe.decode().then(finish, finish);
        else finish();
      };
      probe.onerror = finish;

      var sizes = img.getAttribute('sizes');
      if (sizes) probe.sizes = sizes;
      if (srcset) probe.srcset = srcset;
      else probe.src = src;
    });
  }

  function rowKind(row) {
    var wrap = row.closest('.variant-swiper');
    return wrap && wrap.classList.contains('metal-swiper') ? 'metal' : 'shape';
  }

  /* Where each row was scrolled, read off the card about to be replaced. */
  function rowScroll(card) {
    var out = {};
    var rows = card.querySelectorAll('.variant-swiper .variant-options');
    for (var i = 0; i < rows.length; i++) out[rowKind(rows[i])] = rows[i].scrollLeft;
    return out;
  }

  /* Each new row goes exactly where the old one was, so a shopper working along
     a row keeps their place. Only the row that was TAPPED may then move, and
     only if its chosen swatch would otherwise sit out of sight or under an
     arrow - a tap never hides its own result. The other row is left alone even
     when its active swatch is off-screen: the first cut centred that too, and a
     metal tap then slid the shape row out from under the shopper (measured on
     dev, scrollLeft 0 -> 138), which is exactly the kind of movement nobody
     asked for.

     Instant, not smooth: the strip has scroll-behavior:smooth, and a freshly
     inserted row gliding into place would read as a glitch of its own.

     The synthetic scroll event is the one civia-card-variants.js listens for,
     so the arrows are synced now rather than on the next real scroll. */
  function settleRows(card, scroll, tapped) {
    var rows = card.querySelectorAll('.variant-swiper .variant-options');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var left = scroll[rowKind(row)] || 0;
      var max = Math.max(0, row.scrollWidth - row.clientWidth);
      var active = row.querySelector('.variant-option.active');

      if (active && max > 0 && rowKind(row) === tapped) {
        var from =
          active.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
        var width = active.offsetWidth;
        if (from < left + ARROW_ROOM || from + width > left + row.clientWidth - ARROW_ROOM) {
          left = from - (row.clientWidth - width) / 2;
        }
      }
      left = Math.max(0, Math.min(max, Math.round(left)));

      row.style.scrollBehavior = 'auto';
      row.scrollLeft = left;
      row.style.scrollBehavior = '';

      try {
        row.dispatchEvent(new Event('scroll'));
      } catch (e) {}
    }
  }

  /* Instant feedback: the tapped swatch reads as chosen straight away, not after
     the network. The metal row's inline onclick already does this for metal;
     doing it here as well covers shape, and is harmless twice.

     It also records which row the tap came from, for settleRows. Kept on the
     card, so with several quick taps it is always the latest one's row. */
  function markChosen(swatch) {
    var type = swatch.classList.contains('metal') ? 'metal' : 'shape';
    var card = swatch.closest(CARD);
    if (card) card.__civiaTapped = type;

    var row = swatch.closest('.variant-options');
    if (!row) return;
    var all = row.querySelectorAll('.variant-option.' + type);
    for (var i = 0; i < all.length; i++) all[i].classList.remove('active');
    swatch.classList.add('active');
  }

  function swapCard(card, url) {
    card.__civiaWant = url;
    if (card.getAttribute('data-civia-busy')) return;
    card.setAttribute('data-civia-busy', '1');
    card.classList.add('civia-swapping');

    var fresh = null;

    loadCard(url)
      .then(function (node) {
        fresh = node.cloneNode(true);
        return readyPhoto(fresh);
      })
      .then(function () {
        card.removeAttribute('data-civia-busy');

        /* Gone from the page meanwhile - a filter or sort re-rendered the grid. */
        if (!card.parentNode) return;

        var want = card.__civiaWant;
        if (want !== url) {
          if (cardPath(card) === pathOf(want)) {
            card.classList.remove('civia-swapping');
            return;
          }
          swapCard(card, want);
          return;
        }

        var scroll = rowScroll(card);
        card.parentNode.replaceChild(fresh, card);

        try {
          if (window.civiaCardVariants) window.civiaCardVariants.scan();
        } catch (e) {}
        settleRows(fresh, scroll, card.__civiaTapped);
        try {
          if (window.civiaCard) window.civiaCard.run();
        } catch (e) {}

        var name = fresh.querySelector('.card-title');
        if (name) announce(name.textContent.trim());
      })
      .catch(function () {
        card.removeAttribute('data-civia-busy');
        card.classList.remove('civia-swapping');
        window.location.assign(card.__civiaWant || url);
      });
  }

  /* --- re-running the section's inline scripts ---------------------------- */

  /*
   * Three kinds of script live inside the product section:
   *
   *   JSON blocks      - variant data read by the web components. Never executed,
   *                      so they are left exactly as injected.
   *   External `src`   - already fetched and executed on first load. Re-running
   *                      them would re-register custom elements and throw. The
   *                      registrations are global, so new instances upgrade anyway.
   *   Inline code      - per-product logic (inventory map, ring-size table, the
   *                      gallery's scroll and dots). These must run again.
   *
   * Inline code is written for a cold page load, so most of it is wrapped in
   * `DOMContentLoaded`, an event that will never fire again. `document.addEventListener`
   * is therefore shimmed for the duration: a DOMContentLoaded registration is
   * collected and invoked immediately afterwards, since the DOM is by definition
   * ready. Everything else passes straight through.
   *
   * Scripts that guard themselves (`if (window.__civiaDiamondGuide) return;`) do so
   * because they bind delegated listeners to `document`, which survive the swap
   * untouched. Letting their guard skip them is the correct outcome, not a bug.
   */
  function runScripts(container) {
    var original = document.addEventListener;
    var deferred = [];

    document.addEventListener = function (type, fn, opts) {
      if (type === 'DOMContentLoaded' && typeof fn === 'function') {
        deferred.push(fn);
        return;
      }
      return original.call(document, type, fn, opts);
    };

    try {
      var scripts = container.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var old = scripts[i];
        var type = (old.getAttribute('type') || '').toLowerCase();

        if (old.src) continue;
        if (type.indexOf('json') > -1) continue;

        var fresh = document.createElement('script');
        if (type) fresh.type = type;
        fresh.textContent = old.textContent;
        try {
          old.parentNode.replaceChild(fresh, old);
        } catch (e) {
          /* One bad script must not abandon the rest of the page. */
        }
      }
    } finally {
      document.addEventListener = original;
    }

    deferred.forEach(function (fn) {
      try { fn(); } catch (e) {}
    });
  }

  /* --- the swap ---------------------------------------------------------- */

  function announce(title) {
    var live = document.getElementById('civia-swap-live');
    if (!live) {
      live = document.createElement('p');
      live.id = 'civia-swap-live';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      live.style.cssText =
        'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
        'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
      document.body.appendChild(live);
    }
    live.textContent = title;
  }

  /*
   * Re-executes the scripts listed in RERUN by appending a fresh <script> with
   * the URL the theme already emitted - same hashed asset, so it comes from
   * cache rather than the network. Appending a script element always executes
   * it again, which is the whole point.
   *
   * The previous re-run tag is removed first so repeated swaps leave one tag
   * per script rather than a growing pile.
   */
  function rerun() {
    for (var i = 0; i < RERUN.length; i++) {
      var name = RERUN[i];

      var source = document.querySelector('script[src*="' + name + '."]');
      if (!source) continue;
      var url = source.src;

      var previous = document.querySelector('script[data-civia-rerun="' + name + '"]');
      if (previous && previous.parentNode) previous.parentNode.removeChild(previous);

      var fresh = document.createElement('script');
      fresh.src = url;
      fresh.setAttribute('data-civia-rerun', name);
      document.body.appendChild(fresh);
    }
  }

  /*
   * Round 96 - the rest of the product page follows the swap.
   *
   * Only the main section was ever replaced, so everything below it went on
   * describing the product the page was loaded on. Measured on dev: Silver
   * Oval -> Silver Round kept Silver Oval's reviews, and You May Also Like,
   * which is chosen per product, never changed.
   *
   * The other template sections are fetched for the new product through the
   * Section Rendering API (`sections=`, at most five ids a request) and swapped
   * in - with two deliberate exceptions:
   *
   *   app sections      An app block renders in the browser from data the app
   *                     captured at page load (the reviews app reads
   *                     AIR_REVIEWS.product). Its fresh HTML is an empty shell
   *                     and the app's own re-render hook still draws the
   *                     load-time product. Left alone; civia-sample-reviews-pdp.js
   *                     hides a review widget that no longer matches.
   *   external scripts  A section whose behaviour lives in a <script src> - the
   *                     FAQ accordion, halo-faqs.js - would lose it, because
   *                     runScripts never re-runs external scripts. Those
   *                     sections read the same on every product anyway.
   *
   * A response that lands after a later swap is dropped, so a quick second tap
   * can never paint the first product's sections over the second's.
   */
  var SECTION_BATCH = 5;

  function swapSection(id, html) {
    if (!html || html.indexOf('shopify-app-block') > -1) return;

    var host = document.getElementById('shopify-section-' + id);
    if (!host) return;

    var fresh = parse(html).getElementById('shopify-section-' + id);
    if (!fresh || fresh.querySelector('script[src]')) return;

    host.innerHTML = fresh.innerHTML;
    runScripts(host);

    try {
      host.dispatchEvent(
        new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } })
      );
    } catch (e) {}
  }

  function refreshSections(url) {
    var ids = [];
    var nodes = document.querySelectorAll('[id^="shopify-section-template--"]');

    for (var i = 0; i < nodes.length; i++) {
      if (/__main$/.test(nodes[i].id)) continue;
      ids.push(nodes[i].id.replace(/^shopify-section-/, ''));
    }

    var target = pathOf(url);

    for (var b = 0; b < ids.length; b += SECTION_BATCH) {
      (function (batch) {
        fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'sections=' + batch.join(','), {
          credentials: 'same-origin'
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (json) {
            if (!json || location.pathname !== target) return;
            batch.forEach(function (id) { swapSection(id, json[id]); });
          })
          .catch(function () {});
      })(ids.slice(b, b + SECTION_BATCH));
    }
  }

  function apply(node, url) {
    var current = section();
    if (!current) return false;

    /* The existing wrapper is kept and only its contents replaced, so anything
       outside this file holding a reference to the section element stays valid. */
    current.innerHTML = node.innerHTML;

    runScripts(current);
    rerun();
    refreshSections(url);

    /* The event Shopify's own theme editor fires when it re-renders a section,
       so anything written to the theme convention re-initialises itself without
       needing to be named in RERUN. civia-card-variants.js already listens. */
    try {
      current.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: current.id.replace(/^shopify-section-/, '') }
        })
      );
    } catch (e) {}

    var heading = current.querySelector('.productView-title, h1');
    var name = heading ? heading.textContent.trim() : '';

    /* The full-document path carries the real <title>. The section path does not,
       so the product name is recombined with the store suffix captured at load. */
    var title = node.getAttribute('data-civia-title') || (name ? name + SUFFIX : '');
    if (title) document.title = title;

    if (name) announce(name);

    return true;
  }

  function go(url, push) {
    if (busy) return;
    busy = true;

    var host = section();
    if (host) {
      host.setAttribute('aria-busy', 'true');
      host.classList.add('civia-swapping');
    }

    var done = function () {
      busy = false;
      if (host) {
        host.removeAttribute('aria-busy');
        host.classList.remove('civia-swapping');
      }
    };

    load(url)
      .then(function (node) {
        /* The node is cached and may be swapped in more than once, so hand
           `apply` a clone and leave the cached copy pristine. */
        if (!apply(node.cloneNode(true), url)) throw new Error('no section');
        if (push) history.pushState({ civiaSwap: true }, '', url);
        done();
      })
      .catch(function () {
        done();
        /* Whatever went wrong, the shopper still gets the product. */
        window.location.assign(url);
      });
  }

  /* --- events ------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    /* Let the browser own new-tab, new-window and middle clicks. */
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    /* Collection grid first: a swatch on a card replaces that card and nothing
       else. This also covers card grids that appear elsewhere - "you may also
       like" on a product page, for instance - which is the behaviour you would
       want there anyway. */
    var swatch = event.target.closest(CARD_LINKS);
    if (swatch) {
      var card = swatch.closest(CARD);
      var swatchHref = swatch.getAttribute('href');
      if (!card || !swatchHref || swatchHref.charAt(0) === '#') return;

      var swatchUrl = new URL(swatchHref, location.origin);
      if (swatchUrl.origin !== location.origin) return;

      event.preventDefault();

      /* Re-tapping the shape or metal the card already shows does nothing.
         The test is the card's own link, not the swatch's `active` class: the
         metal swatches carry `onclick="selectVariant(this, 'metal')"`, which
         runs in the target phase - before this delegated listener - and stamps
         `active` on whatever was just clicked. Reading that class here would
         therefore report every metal click as "already active" and swallow it.

         Round 93: while a swap is still loading, a tap back to the product the
         card shows is a real choice - it cancels the pending one - so it goes
         through to swapCard, which resolves it without a request. */
      if (cardPath(card) === swatchUrl.pathname && !card.getAttribute('data-civia-busy')) return;

      markChosen(swatch);
      swapCard(card, swatchUrl.pathname + swatchUrl.search);
      return;
    }

    var link = event.target.closest ? event.target.closest(LINKS) : null;
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    if (link.target && link.target !== '_self') return;
    if (!section()) return;

    var url = new URL(href, location.origin);
    if (url.origin !== location.origin) return;

    event.preventDefault();

    /* Re-selecting the shape or metal already being shown is a no-op, not a
       reload - the old markup left `metal-color`'s dead `onclick` to swallow it. */
    if (samePath(url.href)) return;

    go(url.pathname + url.search, true);
  });

  /* Warming the cache on intent makes the common case land with no visible wait.
     Bounded, same-origin, and only ever a GET of a page the shopper is one click
     from requesting anyway. */
  function warm(event) {
    if (!event.target.closest || cache.size >= PREFETCH_MAX) return;

    var swatch = event.target.closest(CARD_LINKS);
    if (swatch) {
      var warmCard = swatch.closest(CARD);
      if (!warmCard) return;
      var raw = swatch.getAttribute('href');
      if (!raw) return;
      /* Normalised the same way `swapCard` does it, so the warmed entry is the
         one the click actually looks up. */
      var target = new URL(raw, location.origin);
      if (target.origin !== location.origin) return;
      if (cardPath(warmCard) === target.pathname) return;
      var path = target.pathname + target.search;
      if (!cache.has('card:' + path)) loadCard(path).catch(function () {});
      return;
    }

    var link = event.target.closest(LINKS);
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || samePath(href) || cache.has(href)) return;
    load(href).catch(function () {});
  }

  document.addEventListener('mouseover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });

  window.addEventListener('popstate', function (event) {
    if (!event.state || !event.state.civiaSwap) return;
    go(location.pathname + location.search, false);
  });

  /* --- the one style this needs ------------------------------------------ */

  var style = document.createElement('style');
  /* Round 93: a grid card no longer dims as a whole. The photo, title, rating
     and price fade - the parts about to change - while the swatch rows stay
     solid, so the choice just made is the clearest thing on the card, and stay
     tappable, so a change of mind is not swallowed. The fade waits 80ms, so a
     swap served from the warm cache does not flicker at all. */
  style.textContent =
    '.civia-swapping{opacity:.55;transition:opacity .12s ease-out;pointer-events:none}' +
    '.product-item.civia-swapping{opacity:1;transition:none}' +
    '.product-item.civia-swapping .card-product,.product-item.civia-swapping .card__heading,' +
    '.product-item.civia-swapping .civia-cr,.product-item.civia-swapping .card-price' +
    '{opacity:.45;transition:opacity .18s ease-out .08s}' +
    '.product-item.civia-swapping .variant-section{pointer-events:auto}' +
    '@media (prefers-reduced-motion:reduce){.civia-swapping,.product-item.civia-swapping *{transition:none!important}}';
  document.head.appendChild(style);
})();
